import {
  type GraphModelShape,
  type ShowRelatedComponentsShape,
  type ScopedDiff,
  RepresentationData,
} from '@ardoq/data-model';
import {
  aggregateReferences,
  type TraversalAndGroupingResult,
  getIsShowReferenceTypeApplied,
  GraphItemFormattingMaps,
  ItemLabels,
  RawGraphEdge2,
  RawGraphNode2,
  collapseGraph,
  HasGetDistinctReferenceKey,
  RelationshipDiagramsViewInterface,
  graphGroupsMap,
  GraphItem,
  GraphEdgeBase,
  GraphNode,
  GetLabelsDataInterface,
  getGraphNodeOrGroupLabelForViewpointMode,
  getGraphNodeOrGroupLabelForViewsNotSupportingMultilabeling,
  getGraphEdgeLabelForViewpointMode,
  getGraphEdgeLabelForViewsNotSupportingMultilabeling,
  itemLabelToLegacyLabelWithFormatting,
  getComponentGraphNodeMetadata,
  URLFieldValue,
  ComponentGraphNode,
  getComponentGraphGroupMetadata,
  ComponentGraphGroup,
  ComponentGraphEdge,
  getComponentGraphEdgeMetadata,
  getParentChildEdgeMetadata,
  getAggregatedComponentGraphEdgeMetadata,
  ComponentGraphParentChildEdge,
  getUrlFieldValuesByComponentId,
  getUrlFieldValuesByReferenceId,
  getReferenceIdsFromReferenceList,
  GraphInterface,
  SVGColorAttributes,
  DEFAULT_EDGE_COLOR_ATTRIBUTES,
  getAddedAndUpdatedItems,
  GraphItemsModel,
  mapByExcludingNull,
  type PureGraphEdge,
} from '@ardoq/graph';
import type { I18nLocale } from '@ardoq/i18n';
import { LoadedScenarioData } from 'loadedScenarioData$';
import { combineGraphs, getShortestPaths } from 'modelInterface/graph/utils';
import {
  GetGraphItemsDataArgs,
  GetEdgeLabels,
  GetNodeLabels,
  GraphItems,
  GraphItemsData,
  PureGraphItems,
} from './types';
import { logError } from '@ardoq/logging';
import { componentInterface } from '@ardoq/component-interface';
import { isTruthy, WILDCARD } from '@ardoq/common-helpers';
import type { ArdoqId } from '@ardoq/api-types';
import { RelationshipsViewNode } from '../types';

/**
 * Returns a set of component IDs whose parent has changed in the scenario diff.
 * Compares the parent field between branch and master components.
 */
const getScenarioParentChangedComponents = (
  scopeDiff: ScopedDiff | null
): Set<ArdoqId> => {
  if (!scopeDiff) {
    return new Set<ArdoqId>();
  }

  const { branchComponents, masterComponents } = scopeDiff;

  const masterComponentsById = new Map(
    masterComponents.map(comp => [comp._id, comp])
  );

  return new Set(
    branchComponents
      .filter(branchComp => {
        const masterComp = masterComponentsById.get(branchComp._id);
        return masterComp && branchComp.parent !== masterComp.parent;
      })
      .map(branchComp => branchComp._id)
  );
};

interface AddScenarioRelatedComponentsArgs {
  loadedScenarioData: LoadedScenarioData;
  showScopeRelated: boolean;
  scenarioRelatedComponentIds: string[];
  isInScopeDiffMode: boolean;
  componentMap: Map<string, RawGraphNode2>;
  referenceMap: Map<string, RawGraphEdge2>;
  graphModel: GraphModelShape;
}
/** Add scenario related components with their shortest path */
export const addScenarioRelatedComponents = ({
  loadedScenarioData,
  showScopeRelated,
  scenarioRelatedComponentIds,
  isInScopeDiffMode,
  componentMap,
  referenceMap,
  graphModel,
}: AddScenarioRelatedComponentsArgs) => {
  const { scenarioRelatedGraph, scenarioData } = loadedScenarioData;

  if (
    showScopeRelated &&
    scenarioRelatedGraph &&
    scenarioData &&
    scenarioRelatedComponentIds &&
    !isInScopeDiffMode
  ) {
    const {
      connected: { connectedComponents },
    } = scenarioData;
    const connectedComponentsSet = new Set(connectedComponents);
    const targetComponentIds = new Set(componentMap.keys());
    const combinedGraph = combineGraphs(graphModel, scenarioRelatedGraph);
    scenarioRelatedComponentIds.forEach((componentId: string) => {
      const shortestPath = getShortestPaths({
        componentId,
        graph: combinedGraph,
        targetComponentIds,
      });
      if (shortestPath) {
        shortestPath.components.forEach(rawGraphItem =>
          componentMap.set(rawGraphItem.id, {
            ...rawGraphItem,
            isGhost: !connectedComponentsSet.has(rawGraphItem.id),
          })
        );
        shortestPath.references
          .filter(rawGraphItem => !referenceMap.has(rawGraphItem.id))
          .forEach(rawGraphItem => {
            referenceMap.set(rawGraphItem.id, rawGraphItem);
          });
      } else {
        componentMap.set(componentId, {
          id: componentId,
          modelId: componentId,
          name: componentInterface.getDisplayName(componentId) ?? undefined,
        });
      }
    });
  }
};

type GetComponentIdsFromRawGraphArgs = {
  groupMap: Map<string, RawGraphNode2>;
  componentMap: Map<string, RawGraphNode2>;
  viewInterface: GraphInterface;
};
export const getComponentIdsFromRawGraph = ({
  viewInterface,
  componentMap,
  groupMap,
}: GetComponentIdsFromRawGraphArgs) =>
  [...componentMap.values(), ...groupMap.values()]
    .filter(({ modelId }) => modelId && viewInterface.isComponent(modelId))
    .map(({ modelId }) => modelId!);

export type GetRawViewGraphArgs = {
  viewInterface: RelationshipDiagramsViewInterface;
  rawGraph: TraversalAndGroupingResult;
  collapsedGroupIds: Set<string>;
  graphModel: GraphModelShape;
  loadedScenarioData: LoadedScenarioData;
  scenarioRelatedComponents: ShowRelatedComponentsShape;
  isInScopeDiffMode: boolean;
  showScopeRelated: boolean;
  shouldCollapseGraph: boolean;
  i18nLocale: I18nLocale;
} & HasGetDistinctReferenceKey;
/**
 * Apply the view state - specific transformations to the raw graph, such as:
 * adding scenario components, collapsing graph, aggregating references, etc
 */
export const getRawViewGraph = ({
  viewInterface,
  rawGraph,
  collapsedGroupIds,
  graphModel,
  loadedScenarioData,
  scenarioRelatedComponents,
  isInScopeDiffMode,
  showScopeRelated,
  getDistinctReferenceKey,
  shouldCollapseGraph,
  i18nLocale,
}: GetRawViewGraphArgs) => {
  const {
    componentMap: initialComponentMap,
    referenceList: initialReferenceList,
    groupMap: initialGroupMap,
  } = rawGraph;

  const initialReferenceMap = new Map(
    initialReferenceList.map(ref => [ref.id, ref])
  );

  // this modifies the contents of reference and component maps
  addScenarioRelatedComponents({
    loadedScenarioData,
    showScopeRelated,
    isInScopeDiffMode,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
    componentMap: initialComponentMap,
    referenceMap: initialReferenceMap,
    graphModel,
  });

  const {
    componentMap,
    referenceList: collapsedReferenceList,
    groupMap,
    componentIdsOfCollapsedGroups,
  } = shouldCollapseGraph
    ? collapseGraph({
        componentMap: initialComponentMap,
        referenceList: [...initialReferenceMap.values()],
        groupMap: initialGroupMap,
        collapsedGroupIds,
      })
    : {
        componentMap: initialComponentMap,
        referenceList: initialReferenceList.map(reference => ({
          ...reference,
          uncollapsedSourceId: reference.sourceId,
          uncollapsedTargetId: reference.targetId,
        })),
        groupMap: initialGroupMap,
        componentIdsOfCollapsedGroups: [],
      };

  const componentIds = getComponentIdsFromRawGraph({
    viewInterface,
    groupMap,
    componentMap,
  });

  const allReferenceModelIdsFromCollasedGraph =
    getReferenceIdsFromReferenceList(collapsedReferenceList);

  const urlFieldValuesByComponentId = getUrlFieldValuesByComponentId(
    viewInterface,
    componentIds
  );
  const urlFieldValuesByReferenceId = getUrlFieldValuesByReferenceId(
    viewInterface,
    allReferenceModelIdsFromCollasedGraph
  );

  const edgeColorsByReferenceModelId = new Map(
    allReferenceModelIdsFromCollasedGraph.map(referenceModelId => [
      referenceModelId,
      viewInterface.getReferenceCssColors(referenceModelId, {
        useAsBackgroundStyle: false,
      }) ?? DEFAULT_EDGE_COLOR_ATTRIBUTES,
    ])
  );

  const referenceList = aggregateReferences({
    viewInterface,
    referenceList: collapsedReferenceList,
    getDistinctReferenceKey,
    componentMap: initialComponentMap,
    groupMap: initialGroupMap,
    collapsedGroupIds,
    isInScopeDiffMode,
    urlFieldValuesByReferenceId,
    i18nLocale,
  });

  return {
    componentMap,
    groupMap,
    componentIdsOfCollapsedGroups,
    referenceList,
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    edgeColorsByReferenceModelId,
  };
};

type GetLabelsDataBase = {
  isShowReferenceTypeApplied: boolean;
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  rawReferences: RawGraphEdge2[];
  i18nLocale: I18nLocale;
};
export const getViewpointModeLabelsData = (
  args: GetLabelsDataBase & { viewInterface: GetLabelsDataInterface }
) =>
  getLabelsData({
    ...args,
    getGraphNodeOrGroupLabel: item =>
      getGraphNodeOrGroupLabelForViewpointMode(
        args.viewInterface,
        item,
        args.i18nLocale
      ),
    getGraphEdgeLabel: (item, isShowRefType) =>
      getGraphEdgeLabelForViewpointMode(
        args.viewInterface,
        item,
        args.i18nLocale,
        isShowRefType
      ),
  });

/**
 * Resolves multi-labeling data for each graph item and composes the result to
 * reflect the specifics of legacy formatting: only the first other label is
 * included, and only when it corresponds to the first label formatting filter.
 * Views that do not support multi-label display should use this instead of
 * {@link getViewpointModeLabelsData}.
 */
export const getLabelsDataForViewsNotSupportingMultilabeling = (
  args: GetLabelsDataBase & { viewInterface: GetLabelsDataInterface }
) =>
  getLabelsData({
    ...args,
    getGraphNodeOrGroupLabel: item => ({
      mainLabel: itemLabelToLegacyLabelWithFormatting(
        getGraphNodeOrGroupLabelForViewsNotSupportingMultilabeling(
          args.viewInterface,
          item,
          args.i18nLocale
        )
      ),
    }),
    getGraphEdgeLabel: (item, isShowRefType) => ({
      mainLabel: itemLabelToLegacyLabelWithFormatting(
        getGraphEdgeLabelForViewsNotSupportingMultilabeling(
          args.viewInterface,
          item,
          args.i18nLocale,
          isShowRefType
        )
      ),
    }),
  });

type GetLabelsDataArgs = GetLabelsDataBase & {
  getGraphNodeOrGroupLabel: GetNodeLabels;
  getGraphEdgeLabel: GetEdgeLabels;
};
export const getLabelsData = ({
  componentMap,
  groupMap,
  rawReferences,
  getGraphNodeOrGroupLabel,
  getGraphEdgeLabel,
  isShowReferenceTypeApplied,
}: GetLabelsDataArgs) => {
  const nodeLabelsData = new Map(
    [...componentMap, ...groupMap].map(([id, item]) => [
      id,
      getGraphNodeOrGroupLabel(item),
    ])
  );

  const edgeLabelsData = new Map<string, ItemLabels>(
    rawReferences.map(rawGraphItem => [
      rawGraphItem.id,
      getGraphEdgeLabel(rawGraphItem, isShowReferenceTypeApplied),
    ])
  );
  return { nodeLabelsData, edgeLabelsData };
};

export const getGraphItemsData = ({
  viewInterface,
  collapsedGroupIds,
  graphModel,
  loadedScenarioData,
  scenarioRelatedComponents,
  isInScopeDiffMode,
  showScopeRelated,
  getDistinctReferenceKey,
  shouldCollapseGraph,
  rawGraph,
  getGraphNodeOrGroupLabel,
  getGraphEdgeLabel,
  i18nLocale,
}: GetGraphItemsDataArgs): GraphItemsData => {
  const {
    componentMap,
    referenceList,
    groupMap,
    componentIdsOfCollapsedGroups,
    urlFieldValuesByReferenceId,
    edgeColorsByReferenceModelId,
  } = getRawViewGraph({
    viewInterface,
    collapsedGroupIds,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    isInScopeDiffMode,
    showScopeRelated,
    getDistinctReferenceKey,
    shouldCollapseGraph,
    rawGraph,
    i18nLocale,
  });

  const isShowReferenceTypeApplied = getIsShowReferenceTypeApplied(
    viewInterface.getAllReferenceLabelFilters()
  );

  const { nodeLabelsData, edgeLabelsData } = getLabelsData({
    componentMap,
    groupMap,
    rawReferences: referenceList,
    getGraphNodeOrGroupLabel,
    getGraphEdgeLabel,
    isShowReferenceTypeApplied,
    i18nLocale,
  });

  return {
    groupMap,
    componentMap,
    rawReferences: referenceList,
    componentIdsOfCollapsedGroups,
    nodeLabelsData,
    edgeLabelsData,
    isShowReferenceTypeApplied,
    urlFieldValuesByReferenceId,
    edgeColorsByReferenceModelId,
  };
};

/**
 * An interface layer for getting the labels data. In an ideal world it should be just
 * itemLabelData.get(rawGraphItem.id), but we want to ensure the consistency between
 * the raw and view graph items.
 */
const getNodeLabelsFromData = (
  itemLabelData: Map<string, ItemLabels>,
  rawGraphItem: RawGraphNode2,
  getGraphNodeOrGroupLabel: GetNodeLabels
): ItemLabels => {
  const existingData = itemLabelData.get(rawGraphItem.id);

  if (!existingData) {
    logError(new Error('No labels data found in view model for node'));

    const labelsData = getGraphNodeOrGroupLabel(rawGraphItem);

    itemLabelData.set(rawGraphItem.id, labelsData);

    return labelsData;
  }

  return existingData;
};

type GetPureGraphEdgeArgs = {
  rawGraphItem: RawGraphEdge2;
  componentAndGroupMap: Map<string, GraphItem>;
  viewInterface: RelationshipDiagramsViewInterface;
  edgeLabelsData: Map<string, ItemLabels>;
  urlFieldValuesByReferenceId: Map<string, URLFieldValue[] | null>;
  edgeColorsByReferenceModelId: Map<string, SVGColorAttributes>;
  i18nLocale: I18nLocale;
  parentChangedComponentIds?: Set<ArdoqId>;
};

const getPureGraphEdge = ({
  rawGraphItem,
  componentAndGroupMap,
  viewInterface,
  edgeLabelsData,
  urlFieldValuesByReferenceId,
  edgeColorsByReferenceModelId,
  i18nLocale,
  parentChangedComponentIds,
}: GetPureGraphEdgeArgs): PureGraphEdge | null => {
  const {
    id: edgeGraphItemId,
    modelId: referenceModelId,
    modelIds: referenceModelIds,
    sourceId,
    targetId,
    isParentChildReference,
  } = rawGraphItem;

  // Aggregated references (merged after group collapse), including aggregated
  // parent-child. getAggregatedComponentGraphEdgeMetadata ignores the reference model
  // type for parent-child edges, so their synthetic merged IDs (e.g. "parentId-childId")
  // need not resolve; routing them here preserves the aggregate identity
  // (modelIds/isAggregated) the collapsed-group count label depends on.
  if (referenceModelIds) {
    const referenceModelType = isParentChildReference
      ? ComponentGraphParentChildEdge.getType()
      : viewInterface.getReferenceModelType(referenceModelIds[0]);

    if (!referenceModelType) {
      logError(
        new Error('Missing reference model type for aggregated graph edge'),
        null,
        { referenceModelIds, edgeGraphItemId, isParentChildReference }
      );
      return null;
    }

    const metadata = getAggregatedComponentGraphEdgeMetadata({
      viewInterface,
      referenceModelIds,
      referenceModelType,
      edgeId: edgeGraphItemId,
      edgeLabelsData,
      urlFieldValuesByReferenceId,
      edgeColorsByReferenceModelId,
      isParentChildReference,
    });

    if (!metadata) {
      logError(new Error('Missing metadata for aggregated graph edge'));
      return null;
    }

    return ComponentGraphEdge.createAggregated({
      modelIds: referenceModelIds,
      sourceId,
      targetId,
      graphNodeMap: componentAndGroupMap,
      id: edgeGraphItemId,
      metadata,
    });
  }

  // Handle non-aggregated parent-child references
  if (isParentChildReference) {
    const childModelId = componentAndGroupMap.get(targetId)?.modelId;
    const hasScenarioParentChanged = Boolean(
      childModelId && parentChangedComponentIds?.has(childModelId)
    );

    const metadata = getParentChildEdgeMetadata({
      viewInterface,
      sourceId,
      targetId,
      edgeLabelsData,
      graphNodeMap: componentAndGroupMap,
      edgeId: edgeGraphItemId,
      i18nLocale,
      hasScenarioParentChanged,
    });

    return ComponentGraphParentChildEdge.create({
      modelId: referenceModelId,
      sourceId,
      targetId,
      metadata,
      graphNodeMap: componentAndGroupMap,
      id: edgeGraphItemId,
    });
  }

  const referenceModelType = viewInterface.getReferenceModelType(
    referenceModelId ?? ''
  );

  if (!referenceModelId || !referenceModelType) {
    logError(new Error('Missing reference model type for graph edge'), null, {
      referenceModelId,
      edgeGraphItemId,
    });
    return null;
  }
  // Handle regular references
  const metadata = getComponentGraphEdgeMetadata({
    viewInterface,
    referenceModelId: referenceModelId,
    referenceModelType,
    edgeId: edgeGraphItemId,
    edgeLabelsData,
    urlFieldValuesByReferenceId,
    edgeColorsByReferenceModelId,
  });

  return ComponentGraphEdge.create({
    modelId: referenceModelId ?? '',
    sourceId,
    targetId,
    graphNodeMap: componentAndGroupMap,
    id: edgeGraphItemId,
    metadata,
  });
};

const getNodeId = (
  item: RawGraphNode2,
  groupMap: Map<string, RawGraphNode2>
): string => {
  const { id, modelId, parentId } = item;
  if (!parentId) {
    return id;
  }
  const parent = groupMap.get(parentId);
  if (!parent) {
    logError(Error('Parent not found in component map.'));
    return id;
  }
  return `${getNodeId(parent, groupMap)}_${modelId}`;
};

const getGraphNode = (
  rawGraphItem: RawGraphNode2,
  groupMap: Map<string, RawGraphNode2>,
  itemLabels: ItemLabels
) => {
  const { modelId, isGhost } = rawGraphItem;
  const id = getNodeId(rawGraphItem, groupMap);
  return GraphNode.create(modelId!, isGhost, id, itemLabels);
};

type GetPureGraphNodeArgs = GraphItemFormattingMaps & {
  rawGraphItem: RawGraphNode2;
  groupMap: Map<string, RawGraphNode2>;
  viewInterface: RelationshipDiagramsViewInterface;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  nodeLabelsData: Map<string, ItemLabels>;
  contextComponentId: ArdoqId;
};
const getPureGraphNode = ({
  rawGraphItem,
  groupMap,
  viewInterface,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  nodeLabelsData,
  contextComponentId,
}: GetPureGraphNodeArgs) => {
  const { modelId } = rawGraphItem;
  const graphNodeMetaData = getComponentGraphNodeMetadata({
    viewInterface,
    rawGraphItem,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    nodeLabelsData,
    isContext: Boolean(
      contextComponentId && contextComponentId === rawGraphItem.modelId
    ),
  });

  if (!graphNodeMetaData) {
    logError(new Error('Missing metadata for graph node'));
    return null;
  }

  const id = getNodeId(rawGraphItem, groupMap);
  return ComponentGraphNode.create(modelId!, id, graphNodeMetaData);
};

type GetPureGraphGroupArgs = GraphItemFormattingMaps & {
  rawGraphItem: RawGraphNode2;
  viewInterface: RelationshipDiagramsViewInterface;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  nodeLabelsData: Map<string, ItemLabels>;
  isCollapsed: boolean;
  isCollapsible: boolean;
  contextComponentId: ArdoqId;
};
const getPureGraphGroup = ({
  rawGraphItem,
  viewInterface,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  nodeLabelsData,
  isCollapsed,
  isCollapsible,
  contextComponentId,
}: GetPureGraphGroupArgs) => {
  const metadata = getComponentGraphGroupMetadata({
    viewInterface,
    rawGraphItem,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    nodeLabelsData,
    isCollapsed,
    isCollapsible,
    isContext: Boolean(
      contextComponentId && contextComponentId === rawGraphItem.modelId
    ),
  });

  if (!metadata) {
    logError(new Error('Missing metadata for graph group'));
    return null;
  }

  return ComponentGraphGroup.create(rawGraphItem, metadata);
};

type GetPureGraphGroupsMapArgs = GraphItemFormattingMaps & {
  groupMap: Map<string, RawGraphNode2>;
  viewInterface: RelationshipDiagramsViewInterface;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  collapsedGroupIds: Set<string>;
  isGraphCollapsible: boolean;
  nodeLabelsData: Map<string, ItemLabels>;
  contextComponentId: ArdoqId;
};

const pureGraphGroupsMap = ({
  groupMap,
  viewInterface,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  collapsedGroupIds,
  isGraphCollapsible,
  nodeLabelsData,
  contextComponentId,
}: GetPureGraphGroupsMapArgs) =>
  new Map(
    Array.from(groupMap.values())
      .map(rawGraphItem => {
        const group = getPureGraphGroup({
          rawGraphItem,
          viewInterface,
          urlFieldValuesByComponentId,
          representationDataByComponentId,
          nodeColorsByGraphItemId,
          conditionalFormattingState,
          nodeLabelsData,
          isCollapsible: isGraphCollapsible,
          isCollapsed:
            collapsedGroupIds.has(WILDCARD) ||
            collapsedGroupIds.has(rawGraphItem.id),
          contextComponentId,
        });

        return group ? ([rawGraphItem.id, group] as const) : null;
      })
      .filter(isTruthy)
  );

type GetGraphItemsArgs = GraphItemsData & {
  viewInterface: RelationshipDiagramsViewInterface;
  rawCollapsedGroupIds: Set<string>;
  getGraphNodeOrGroupLabel: GetNodeLabels;
  scopeDiff: ScopedDiff | null;
  i18nLocale: I18nLocale;
  contextComponentId: ArdoqId;
};
export const getGraphItems = ({
  viewInterface,
  groupMap,
  componentMap,
  rawReferences,
  nodeLabelsData,
  edgeLabelsData,
  rawCollapsedGroupIds,
  getGraphNodeOrGroupLabel,
  urlFieldValuesByReferenceId,
  edgeColorsByReferenceModelId,
  scopeDiff,
  i18nLocale,
  contextComponentId,
}: GetGraphItemsArgs): GraphItems => {
  const groupNodes = graphGroupsMap(
    groupMap,
    rawCollapsedGroupIds,
    nodeLabelsData,
    contextComponentId
  );
  const rawGroups = Array.from(groupMap.values());
  rawGroups.forEach(
    ({ id, parentId }) =>
      (groupNodes.get(id)!.parent = groupNodes.get(parentId!))
  );

  const rawComponents = Array.from(componentMap.values());

  const graphNodes = new Map(
    rawComponents.map(rawGraphItem => {
      const itemLabels = getNodeLabelsFromData(
        nodeLabelsData,
        rawGraphItem,
        getGraphNodeOrGroupLabel
      );

      return [
        rawGraphItem.id,
        getGraphNode(rawGraphItem, groupMap, itemLabels),
      ];
    })
  );
  rawComponents.forEach(
    ({ id, parentId }) =>
      (graphNodes.get(id)!.parent = groupNodes.get(parentId!))
  );

  const componentAndGroupMap = new Map<string, GraphItem>([
    ...graphNodes,
    ...groupNodes,
  ]);

  const parentChangedComponentIds =
    getScenarioParentChangedComponents(scopeDiff);

  const edges = rawReferences
    .map(rawGraphItem =>
      getPureGraphEdge({
        rawGraphItem,
        componentAndGroupMap,
        viewInterface,
        edgeLabelsData,
        urlFieldValuesByReferenceId,
        edgeColorsByReferenceModelId,
        parentChangedComponentIds,
        i18nLocale,
      })
    )
    .filter(isTruthy);

  return {
    edges,
    rawComponents,
    graphNodes,
    rawGroups,
    groupNodes,
  };
};

type GetPureGraphItemsArgs = GraphItemFormattingMaps & {
  i18nLocale: I18nLocale;
  viewInterface: RelationshipDiagramsViewInterface;
  groupMap: Map<string, RawGraphNode2>;
  componentMap: Map<string, RawGraphNode2>;
  rawReferences: RawGraphEdge2[];
  componentIdsOfCollapsedGroups: string[];
  rawCollapsedGroupIds: Set<string>;
  nodeLabelsData: Map<string, ItemLabels>;
  edgeLabelsData: Map<string, ItemLabels>;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  urlFieldValuesByReferenceId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
  edgeColorsByReferenceModelId: Map<string, SVGColorAttributes>;
  scopeDiff: ScopedDiff | null;
  contextComponentId: ArdoqId;
  isGraphCollapsible?: boolean;
};

/**
 * This function is a a substitute for getGraphItems. It uses the pure ComponentGraph classes, which don't
 * resolve data in their methods. This requires passing the correct data via the constructor args or setters.
 *
 * isCollapsible is true by default in all graph groups, set isGraphCollapsible explicitly to false if it shouldn't be.
 */
export const getPureGraphItems = ({
  viewInterface,
  groupMap,
  componentMap,
  rawReferences,
  nodeLabelsData,
  edgeLabelsData,
  urlFieldValuesByComponentId,
  rawCollapsedGroupIds,
  urlFieldValuesByReferenceId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
  edgeColorsByReferenceModelId,
  scopeDiff,
  contextComponentId,
  i18nLocale,
  isGraphCollapsible = true,
}: GetPureGraphItemsArgs): PureGraphItems => {
  const rawGroups = Array.from(groupMap.values());
  const rawComponents = Array.from(componentMap.values());

  const groupNodes = pureGraphGroupsMap({
    groupMap,
    collapsedGroupIds: rawCollapsedGroupIds,
    isGraphCollapsible,
    nodeLabelsData,
    viewInterface,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    contextComponentId,
  });
  rawGroups.forEach(
    ({ id, parentId }) =>
      (groupNodes.get(id)!.parent = groupNodes.get(parentId!))
  );

  const graphNodes = new Map(
    rawComponents
      .map(rawGraphItem => {
        const node = getPureGraphNode({
          rawGraphItem,
          groupMap,
          viewInterface,
          urlFieldValuesByComponentId,
          representationDataByComponentId,
          nodeColorsByGraphItemId,
          conditionalFormattingState,
          nodeLabelsData,
          contextComponentId,
        });
        return node ? ([rawGraphItem.id, node] as const) : null;
      })
      .filter(isTruthy)
  );
  rawComponents.forEach(
    ({ id, parentId }) =>
      (graphNodes.get(id)!.parent = groupNodes.get(parentId!))
  );

  const componentAndGroupMap = new Map<string, GraphItem>([
    ...graphNodes,
    ...groupNodes,
  ]);

  const parentChangedComponentIds =
    getScenarioParentChangedComponents(scopeDiff);

  const edges = rawReferences
    .map(rawGraphItem =>
      getPureGraphEdge({
        rawGraphItem,
        componentAndGroupMap,
        viewInterface,
        edgeLabelsData,
        urlFieldValuesByReferenceId,
        edgeColorsByReferenceModelId,
        i18nLocale,
        parentChangedComponentIds,
      })
    )
    .filter(isTruthy);

  return {
    edges,
    graphNodes,
    groupNodes,
  };
};

type PartitionGraphItemsArgs<
  TGroup extends GraphItem,
  TLeaf extends GraphItem,
  TEdge extends GraphEdgeBase,
> = {
  graphGroups: GraphItemsModel<TGroup>;
  graphNodes: GraphItemsModel<TLeaf>;
  graphEdges: GraphItemsModel<TEdge>;
};

type GraphItemPartitions<
  TGroup extends GraphItem,
  TLeaf extends GraphItem,
  TEdge extends GraphEdgeBase,
> = {
  rootGroups: TGroup[];
  rootGraphNodes: TLeaf[];
  allGraphEdges: TEdge[];
  componentIds: string[];
  groupChildren: Map<TGroup, (TGroup | TLeaf)[]>;
};

export const partitionGraphItemsHierarchy = <
  TGroup extends GraphItem,
  TLeaf extends GraphItem,
  TEdge extends GraphEdgeBase,
>({
  graphGroups,
  graphNodes,
  graphEdges,
}: PartitionGraphItemsArgs<TGroup, TLeaf, TEdge>): GraphItemPartitions<
  TGroup,
  TLeaf,
  TEdge
> => {
  const allGraphGroups = getAddedAndUpdatedItems(graphGroups);
  const allGraphLeaves = getAddedAndUpdatedItems(graphNodes);
  const allGraphNodes = [...allGraphGroups, ...allGraphLeaves];
  const allGraphEdges = getAddedAndUpdatedItems(graphEdges);

  const rootGroups = allGraphGroups.filter(({ parent }) => !parent);
  const rootGraphNodes = allGraphLeaves.filter(({ parent }) => !parent);

  const componentIds = allGraphNodes
    .filter(node => node.isComponent())
    .map(({ modelId }) => modelId);

  const groupChildren = mapByExcludingNull(
    allGraphNodes,
    graphNode => graphNode.parent as TGroup | null
  );

  return {
    rootGroups,
    rootGraphNodes,
    allGraphEdges,
    componentIds,
    groupChildren,
  };
};

type HasSourceAndTarget<TNode> = {
  source: TNode;
  target: TNode;
};

export const buildNodeLinksMap = <
  TNode,
  TLink extends HasSourceAndTarget<TNode>,
>(
  links: TLink[]
): Map<TNode, TLink[]> =>
  links.reduce((result: Map<TNode, TLink[]>, link) => {
    [link.source, link.target].forEach(node => {
      if (!result.has(node)) {
        result.set(node, []);
      }
      result.get(node)!.push(link);
    });
    return result;
  }, new Map());

export const augmentNodeWithLinks = <
  TNode extends RelationshipsViewNode<TNode, TLink>,
  TLink extends { source: TNode; target: TNode },
>(
  node: TNode,
  nodeLinks: Map<TNode, TLink[]>
) => {
  node.links = nodeLinks.get(node) ?? null;
  node.children?.forEach(childNode =>
    augmentNodeWithLinks(childNode, nodeLinks)
  );
};
